import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'

const INSTRUMENTOS_GRUPOS = {
  'Cordas / Harmonia': ['Violão', 'Guitarra', 'Baixo', 'Teclado', 'Piano', 'Cavaquinho', 'Ukulele'],
  'Percussão': ['Bateria', 'Percussão'],
  'Madeiras': ['Flauta', 'Flauta Piccolo', 'Oboé', 'Corne Inglês', 'Clarinete', 'Clarinete Alto', 'Clarinete Baixo', 'Fagote', 'Contrafagote', 'Saxofone Soprano', 'Saxofone Alto', 'Saxofone Tenor', 'Saxofone Barítono', 'Saxofone Baixo'],
  'Metais': ['Trompa', 'Trompete', 'Corneta', 'Trombone', 'Trombone Alto', 'Trombone Tenor', 'Trombone Baixo', 'Eufônio', 'Bombardino', 'Tuba', 'Tuba Baixo'],
  'Cordas Clássicas': ['Violino', 'Viola', 'Violoncelo', 'Contrabaixo'],
  'Outros': ['Voz', 'Outro'],
}

export default function AuthPage() {
  const [modo, setModo] = useState('login') // 'login' | 'cadastro' | 'recuperar' | 'nova-senha'
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({ email: '', password: '', novaSenha: '', confirmarSenha: '', nome: '', instrumento: 'Violão' })
  const [emailEnviado, setEmailEnviado] = useState(false)
  const todosInstrumentos = Object.values(INSTRUMENTOS_GRUPOS).flat()
  const { signIn, signUp, isPasswordRecovery } = useAuth()
  const navigate = useNavigate()

  // Detecta modo via URL param (chegou pelo link do email de recuperação)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('modo') === 'nova-senha') {
      setModo('nova-senha')
    }
  }, [])

  // Detecta quando usuário chega via link de recuperação de senha
  // Cobre dois casos:
  //   1. isPasswordRecovery já é true (evento foi captado pelo useAuth antes de AuthPage montar)
  //   2. Evento PASSWORD_RECOVERY chega depois que AuthPage já está montado
  useEffect(() => {
    if (isPasswordRecovery) setModo('nova-senha')
  }, [isPasswordRecovery])

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setModo('nova-senha')
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  async function handleNovaSenha(e) {
    e.preventDefault()
    if (form.novaSenha.length < 6) { toast.error('Senha deve ter mínimo 6 caracteres'); return }
    if (form.novaSenha !== form.confirmarSenha) { toast.error('As senhas não coincidem'); return }
    setLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ password: form.novaSenha })
      if (error) throw error
      toast.success('Senha alterada com sucesso!')
      navigate('/grupos')
    } catch (err) {
      toast.error(err.message || 'Erro ao alterar senha')
    } finally {
      setLoading(false)
    }
  }

  async function handleRecuperar(e) {
    e.preventDefault()
    if (!form.email.trim()) { toast.error('Digite seu e-mail'); return }
    setLoading(true)
    try {
      // redirectTo deve estar na allowlist exata do Supabase Dashboard → Authentication → URL Configuration
      // Use apenas o origin + /auth sem query params para evitar rejeição por mismatch
      const { error } = await supabase.auth.resetPasswordForEmail(form.email, {
        redirectTo: `${window.location.origin}/auth`
      })
      if (error) throw error
      setEmailEnviado(true)
      toast.success('E-mail enviado! Verifique sua caixa de entrada.')
    } catch (err) {
      toast.error(err.message || 'Erro ao enviar e-mail')
    } finally {
      setLoading(false)
    }
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true)
    try {
      if (modo === 'login') {
        await signIn(form.email, form.password)
        toast.success('Bem-vindo de volta!')
        navigate('/grupos')
      } else {
        if (!form.nome.trim()) { toast.error('Digite seu nome'); return }
        await signUp(form.email, form.password, form.nome, form.instrumento)
        toast.success('Conta criada! Verifique seu email.')
        setModo('login')
      }
    } catch (err) {
      const msgs = {
        'Invalid login credentials': 'Email ou senha incorretos',
        'User already registered': 'Este email já está cadastrado',
        'Password should be at least 6 characters': 'Senha deve ter mínimo 6 caracteres'
      }
      toast.error(msgs[err.message] || err.message)
    } finally {
      setLoading(false)
    }
  }

  // Tela de recuperação de senha
  if (modo === 'recuperar') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 20px', background: 'var(--brand)' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ width: 88, height: 88, margin: '0 auto 12px' }}>
            <img src="/logo-app.png" alt="CifrasBand" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 32, fontWeight: 800, letterSpacing: 1 }}>
            <span style={{ color: '#00CFFF' }}>CIFRAS</span><span style={{ color: '#FF8C00' }}>BAND</span>
          </div>
        </div>

        <div style={{ width: '100%', maxWidth: 400 }}>
          <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: '28px 24px' }}>
            <div style={{ fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
              🔑 Recuperar senha
            </div>

            {emailEnviado ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📧</div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>E-mail enviado!</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 24 }}>
                  Verifique sua caixa de entrada em <strong style={{ color: '#00CFFF' }}>{form.email}</strong> e clique no link para redefinir sua senha.
                </div>
                <button onClick={() => setModo('login')} className="btn btn-primary btn-block" style={{ height: 48 }}>
                  Voltar ao login
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.5 }}>
                  Digite seu e-mail cadastrado e enviaremos um link para você criar uma nova senha.
                </div>
                <form onSubmit={handleRecuperar}>
                  <div className="input-group" style={{ marginBottom: 20 }}>
                    <label className="input-label">E-mail</label>
                    <input className="input" type="email" placeholder="seu@email.com"
                      value={form.email} onChange={e => set('email', e.target.value)} required autoFocus />
                  </div>
                  <button type="submit" className="btn btn-primary btn-block" disabled={loading} style={{ height: 50, fontSize: 15, fontWeight: 700 }}>
                    {loading ? <span className="spinner" /> : 'Enviar link de recuperação'}
                  </button>
                </form>
                <button onClick={() => setModo('login')}
                  style={{ width: '100%', marginTop: 12, background: 'none', border: 'none', color: 'var(--muted)', fontSize: 13, cursor: 'pointer', padding: '8px 0' }}>
                  ← Voltar ao login
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Tela de nova senha (chegou pelo link do email)
  if (modo === 'nova-senha') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 20px', background: 'var(--brand)' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ width: 88, height: 88, margin: '0 auto 12px' }}>
            <img src="/logo-app.png" alt="CifrasBand" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 32, fontWeight: 800, letterSpacing: 1 }}>
            <span style={{ color: '#00CFFF' }}>CIFRAS</span><span style={{ color: '#FF8C00' }}>BAND</span>
          </div>
        </div>

        <div style={{ width: '100%', maxWidth: 400 }}>
          <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: '28px 24px' }}>
            <div style={{ fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
              🔒 Criar nova senha
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.5 }}>
              Escolha uma senha segura com pelo menos 6 caracteres.
            </div>
            <form onSubmit={handleNovaSenha}>
              <div className="input-group">
                <label className="input-label">Nova senha</label>
                <input className="input" type="password" placeholder="Mínimo 6 caracteres"
                  value={form.novaSenha} onChange={e => set('novaSenha', e.target.value)}
                  required minLength={6} autoFocus />
              </div>
              <div className="input-group" style={{ marginBottom: 24 }}>
                <label className="input-label">Confirmar nova senha</label>
                <input className="input" type="password" placeholder="Repita a senha"
                  value={form.confirmarSenha} onChange={e => set('confirmarSenha', e.target.value)}
                  required minLength={6} />
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={loading}
                style={{ height: 50, fontSize: 15, fontWeight: 700 }}>
                {loading ? <span className="spinner" /> : '✅ Salvar nova senha'}
              </button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 20px', background: 'var(--brand)' }}>
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        {/* Ícone SVG */}
        <div style={{ width: 88, height: 88, margin: '0 auto 12px' }}>
          <img src="/logo-app.png" alt="CifrasBand" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
        <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 32, fontWeight: 800, letterSpacing: 1 }}>
          <span style={{ color: '#00CFFF' }}>CIFRAS</span><span style={{ color: '#FF8C00' }}>BAND</span>
        </div>
        <div style={{ fontSize: 11, color: 'rgba(0,207,255,0.55)', marginTop: 4, letterSpacing: 2.5, fontWeight: 500, textTransform: 'uppercase' }}>
          Sincronia que se ouve
        </div>
      </div>

      {/* Form */}
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Tabs */}
        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', borderRadius: 14, padding: 4, marginBottom: 24 }}>
          {['login', 'cadastro'].map(m => (
            <button key={m} onClick={() => setModo(m)} style={{
              flex: 1, padding: '10px', borderRadius: 11, border: 'none', cursor: 'pointer',
              fontFamily: 'Montserrat, sans-serif', fontSize: 14, fontWeight: 600, transition: 'all 0.2s',
              background: modo === m ? 'var(--brand2)' : 'transparent',
              color: modo === m ? '#fff' : 'var(--muted)'
            }}>
              {m === 'login' ? 'Entrar' : 'Cadastrar'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {modo === 'cadastro' && (
            <>
              <div className="input-group">
                <label className="input-label">Seu nome</label>
                <input className="input" type="text" placeholder="Ex: João Silva" value={form.nome}
                  onChange={e => set('nome', e.target.value)} required />
              </div>
              <div className="input-group">
                <label className="input-label">Instrumento</label>
                <select className="input" value={form.instrumento} onChange={e => set('instrumento', e.target.value)}>
                  {Object.entries(INSTRUMENTOS_GRUPOS).map(([grupo, itens]) => (
                    <optgroup key={grupo} label={grupo}>
                      {itens.map(i => <option key={i} value={i}>{i}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="input-group">
            <label className="input-label">Email</label>
            <input className="input" type="email" placeholder="seu@email.com" value={form.email}
              onChange={e => set('email', e.target.value)} required autoComplete="email" />
          </div>

          <div className="input-group">
            <label className="input-label">Senha</label>
            <input className="input" type="password" placeholder="Mínimo 6 caracteres" value={form.password}
              onChange={e => set('password', e.target.value)} required minLength={6} />
          </div>

          {modo === 'login' && (
            <div style={{ textAlign: 'right', marginBottom: 20, marginTop: 6 }}>
              <button type="button" onClick={() => { setModo('recuperar'); setEmailEnviado(false) }}
                style={{ background: 'none', border: 'none', color: '#00CFFF', fontSize: 13, cursor: 'pointer', padding: 0 }}>
                Esqueci minha senha
              </button>
            </div>
          )}

          {modo === 'cadastro' && <div style={{ marginBottom: 24 }} />}

          <button type="submit" className="btn btn-primary btn-block" disabled={loading}
            style={{ height: 52, fontSize: 16, fontWeight: 700 }}>
            {loading ? <span className="spinner" /> : modo === 'login' ? '🎵 Entrar' : '🎸 Criar conta'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--muted)' }}>
          Grupo fechado — apenas por convite
        </div>
      </div>
    </div>
  )
}
